/* eslint-disable no-console */
/* eslint-disable no-underscore-dangle */
import forEach from 'lodash/forEach';
import EventEmitter from 'eventemitter3';

import streamEvents from './constants/streamEvents';
import playerMethods from './constants/playerMethods';
import htmlMediaEvents from './constants/htmlMediaEvents';

import PlayerLoader from './PlayerLoader';
import Timer from '../utils/Timer';

import getStartFromPosition from './utils/getStartFromPosition';
import errorCodes from '../utils/errorCodes';
import debug from '../debugger';
import hlsEvents from './hlsjs/constants/hlsEvents';
import { sendTimingEvent } from '../loggers/timing';
import {
  METADATA_ERROR_NAME,
  WEB_MEDIA_PLAYER_ICY_METADATA_ERROR,
} from './webmediaplayer/constants';
import logError from '../loggers/logError';
import { parseIcyMetadata } from '../utils/parseIcyMetadata';

/**
 * The StreamPlayer interface that is responsible for exposing methods
 * that allow the web-tuner to manipulate the underlying media. This
 * class is also responsible for exposing specific tunerChannel emitter
 * methods that allow the rest of the web-tuner to listen and perform
 * actions based on the state events that are emitted from the currently
 * loaded media.
 * @module StreamPlayer
 */
class StreamPlayer {
  constructor(config) {
    this.player = null;
    this.stream = {};
    this.events = streamEvents;

    this.configuration = config;
    const { volume, timeout, blankMp3Url } = config;

    this.volume = volume || 1;
    this.muted = false;
    this.streamTimeout = timeout || 0;
    this.blankMp3Url = blankMp3Url || '';

    this.timer = new Timer();
    this.eventEmitter = new EventEmitter();
    this.playerLoader = new PlayerLoader(config);
    this.preloadCache = config.preloadCache;
  }

  // TODO: Add support for HLS metadata from web-media-player
  onMetadata(metadata) {
    if (metadata.icy) {
      const parsedMetadata = parseIcyMetadata(metadata.icy);

      this.configuration.nowPlayingPoller.setIsUsingIcyMetadata(true);
      this.eventEmitter.emit(streamEvents.metadata, parsedMetadata);
    }

    if (metadata.id3) {
      this._onMetadata(null, { frag: metadata.id3 });
    }
  }

  onMetadataError(error) {
    const { isWebMediaPlayerEnabled } = this.configuration;

    // handle icy metadata errors
    if (isWebMediaPlayerEnabled && error?.error?.name === METADATA_ERROR_NAME) {
      this.configuration.nowPlayingPoller.setIsUsingIcyMetadata(false);
      logError(WEB_MEDIA_PLAYER_ICY_METADATA_ERROR, error.error);
    }
  }

  /**
   * Method to provide player instances with a "hook" that is called synchronously
   * from the user gesture event handler. This becomes necessary on mobile and Safari
   * desktop browsers due to the autoplay restrictions that exist for asynchronous
   * playback within click handlers. Player instances can implement a .preload method that
   * will be called synchronously here before any asynchronous code. This "hook" allows us
   * preload a blank mp3 into the players synchronously, and then asynchronously switch the
   * source with the desired stream url after Tune.ashx API call to retrieve stream urls resolves.
   *
   * See Apple documentation here:
   *  https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/Using_HTML5_Audio_Video/Device-SpecificConsiderations/Device-SpecificConsiderations.html#//apple_ref/doc/uid/TP40009523-CH5-SW4
   *  @function preload
   */
  preload = async (guideId, streamData) => {
    if (!guideId) {
      this.playerLoader.allPlayers.forEach((player) => {
        if (player[playerMethods.preload]) {
          player[playerMethods.preload](this.blankMp3Url);
        }
      });

      return;
    }

    let players;

    try {
      const stream = streamData.streams[0];
      players = await this.playerLoader.getPlayers(stream);

      if (!players?.length) {
        return;
      }

      streamData.players = players;
      streamData.loadPromise = Promise.race([
        players[0].load(
          stream.url,
          stream.format,
          0,
          streamData.trackingUrl,
          true,
        ),
        new Promise((resolve, reject) =>
          setTimeout(reject, this.configuration.timeout),
        ),
      ]);

      this.preloadCache.set(guideId, streamData);

      await streamData.loadPromise;
    } catch (error) {
      players?.forEach((player) => player?.destroy());
      this.preloadCache.delete(guideId);
    }
  };

  /**
   * Try to play the stream
   * @function attemptPlayback
   * @param playbackConfig {{
   *    stream: Object,
   *    playFromPosition: Number,
   *    additionalTimeout: Number,
   *    ignoreStreamPosition: Boolean,
   *    liveSeekStreamPosition: Number
   *    }}
   * @returns {Object<{ duration: Number }>}
   */
  attemptPlayback = async (playbackConfig) => {
    const {
      stream = {},
      playFromPosition,
      additionalTimeout,
      ignoreStreamPosition,
      liveSeekStreamPosition,
      instreamTrackingUrl,
    } = playbackConfig;
    const { url, format, position, parentGuideId } = stream;

    this.wasPlaying = false;
    this.preloadStreamData =
      this.preloadCache.pluckCachedStreamData(parentGuideId);

    const playbackPosition = getStartFromPosition({
      position,
      playFromPosition,
      ignoreStreamPosition,
      liveSeekStreamPosition,
    });
    const options = {
      url,
      format,
      playbackPosition,
      additionalTimeout,
      instreamTrackingUrl,
    };

    const start = Date.now();
    try {
      await this._attemptStreamPlayback(stream, options);
      return { duration: Date.now() - start };
    } catch (ex) {
      ex.errorData = { duration: Date.now() - start };
      throw ex;
    }
  };

  /**
   * Given a nowPlaying model, this setTitle method builds a formatted title
   * from the title and subtitle of the nowPlaying model.
   * @function setTitle
   * @param nowPlaying {Object}
   */
  setTitle = (nowPlaying) => {
    const title = nowPlaying
      ? `${nowPlaying.title} - ${nowPlaying.subtitle}`
      : null;
    this._invokePlayerMethod(playerMethods.setTitle, title);
  };

  /**
   * Play the stream
   * @event module:StreamPlayer~play
   * @returns {Promise}
   */
  play = async () => {
    await this._monitorForStreamTimeout(
      this._invokePlayerMethod(playerMethods.play),
    );

    this.wasPlaying = true;
  };

  /**
   * @event module:StreamPlayer~pause
   */
  pause = () => {
    this.wasPlaying = false;
    this._invokePlayerMethod(playerMethods.pause);
  };

  /**
   * @event module:StreamPlayer~stop
   */
  stop = () => {
    this._invokePlayerMethod(playerMethods.stop);
  };

  canChangePlaybackRate = () =>
    this._invokePlayerMethod(playerMethods.canChangePlaybackRate);

  changePlaybackRate = (rate = 1.0) => {
    const currentPlaybackRate = this._invokePlayerMethod(
      playerMethods.getPlaybackRate,
    );

    if (rate === currentPlaybackRate) {
      return;
    }

    this.pause();
    this._invokePlayerMethod(playerMethods.changePlaybackRate, rate);
    this.play();
  };

  /**
   * Method responsible for calling setVolume on the chosen media player. Each media
   * player implementation is responsible for formatting the volume depending on the
   * requirements of the media player library that it is using.
   * @event module:StreamPlayer~setVolume
   * @param newVolume {Number}
   */
  setVolume = (newVolume) => {
    this.volume = newVolume;
    this._invokePlayerMethod(playerMethods.setVolume, newVolume);
  };

  /**
   * Get the volume
   * @returns {Number}
   */
  getVolume = () => this.volume;

  /**
   * Toggle the muted state
   * @event module:StreamPlayer~toggleMute
   */
  toggleMute = () => {
    this.muted = !this.muted;
    this._invokePlayerMethod(playerMethods.toggleMute);
  };

  /**
   * @returns {{ elapsedPercent: Number, elapsedSeconds: Number, duration: Number }}
   */
  getTimes = () => this._invokePlayerMethod(playerMethods.getTimes);

  /**
   * Method responsible for clearing the tunerChannel of registered event handlers,
   * and calling clearMedia on the currently loaded player.
   * @function clearMedia
   */
  clearMedia = () => {
    this.clearTunerChannel();
    this._invokePlayerMethod(playerMethods.unload);
  };

  /**
   * Scrub player to specified position
   * @event module:StreamPlayer~seek
   * @param position {Number}
   */
  seek = (position) => {
    this._invokePlayerMethod(playerMethods.seek, position);
  };

  /**
   * Method used to clear the current timer that is monitoring a stream playback attempt
   * in ._monitorForStreamTimeout.
   */
  clearTimeout = () => {
    this.timer.clear();
  };

  /**
   * Get the current stream ID
   * @returns {String} The stream ID
   */
  getCurrentStreamId = () => this.stream.id;

  /**
   * Get the current stream
   * @returns {Object} The stream
   */
  getCurrentStream = () => this.stream;

  /**
   * Get the StreamPlayer configuration.
   * @returns {Object} The configuaration object used for the current player
   */
  getPlayerConfig = () => this.configuration;

  requestInstreamAds = () => {
    this._invokePlayerMethod(playerMethods.requestInstreamAds);
  };

  /**
   * Utility method to clear the tunerChannel of all streamEvents handlers.
   * @function clearTunerChannel
   */
  clearTunerChannel = () => {
    forEach(streamEvents, (event) => this.removeAllListeners(event));
  };

  /**
   * Set all the player listeners
   * @function setPlayerListeners
   */
  setPlayerListeners = () => {
    this._toggleEventListeners(playerMethods.on);
  };

  /**
   * Clear all registered player listeners
   * @function clearPlayerListeners
   */
  clearPlayerListeners = () => {
    this._toggleEventListeners(playerMethods.off);
    this._toggleMetadataListener(playerMethods.off);
  };

  _toggleEventListeners = (onOrOff) => {
    if (this.stream.useStreamMetadata) {
      this._invokePlayerMethod(
        onOrOff,
        hlsEvents.FRAG_PARSING_METADATA,
        this._onMetadata,
      );
    }
    this._invokePlayerMethod(onOrOff, htmlMediaEvents.seeked, this._onSeeked);
    this._invokePlayerMethod(
      onOrOff,
      htmlMediaEvents.progress,
      this._onProgress,
    );
    this._invokePlayerMethod(onOrOff, htmlMediaEvents.playing, this._onPlay);
    this._invokePlayerMethod(onOrOff, htmlMediaEvents.play, this._onPlay);
    this._invokePlayerMethod(onOrOff, htmlMediaEvents.waiting, this._onWaiting);
    this._invokePlayerMethod(
      onOrOff,
      htmlMediaEvents.timeupdate,
      this._onTimeUpdate,
    );
    this._invokePlayerMethod(onOrOff, htmlMediaEvents.ended, this._onEnded);
    this._invokePlayerMethod(onOrOff, htmlMediaEvents.error, this._onError);
    this._invokePlayerMethod(
      onOrOff,
      htmlMediaEvents.retrying,
      this._onRetrying,
    );
  };

  _toggleMetadataListener = (onOrOff) => {
    this._invokePlayerMethod(
      onOrOff,
      htmlMediaEvents.metadata,
      this.onMetadata.bind(this),
    );
    this._invokePlayerMethod(
      onOrOff,
      htmlMediaEvents.error,
      this.onMetadataError.bind(this),
    );
  };

  _onMetadata = async (_, data) => {
    if (!data?.frag?.relurl) {
      return;
    }

    try {
      const mm = await import('@tunein/music-metadata-browser');
      const meta = await mm.fetchFromUrl(data.frag.relurl);

      this.eventEmitter.emit(streamEvents.meta, meta);
    } catch (e) {
      // noop
    }
  };

  _onSeeked = (event) => {
    this.eventEmitter.emit(streamEvents.seeked, event);
  };

  _onPlay = (event) => {
    // Occasionally, a player may emit a waiting event and immediately recover and emit a playing
    // event faster than we can enter the waiting state and register a listener for the play event
    // emitted below. Setting a timeout here buys us time to enter the waiting state and register
    // listeners before below we emit the play event.
    setTimeout(() => {
      this.eventEmitter.emit(streamEvents.play, event);
    });
  };

  _onProgress = (event) => {
    this.eventEmitter.emit(streamEvents.buffer, event);
  };

  _onTimeUpdate = () => {
    this.eventEmitter.emit(streamEvents.positionChanged, this.getTimes());
  };

  _onWaiting = (event) => {
    this.eventEmitter.emit(streamEvents.waiting, event);
  };

  _onEnded = () => {
    this.eventEmitter.emit(streamEvents.mediaEnded, this.stream);
  };

  _onError = (error = {}, message = '') => {
    const { isWebMediaPlayerEnabled, abTestIds } = this.configuration;
    const { id: streamId, url, format } = this.stream;

    // Web Media Player can emit non-fatal errors
    if (error.error?.fatal === false || error.errorData?.fatal === false) {
      return;
    }

    // just in case error/message are large, plain objects or contain self-references.
    // eventKeys will help us debug and code against potential edge cases.
    const errorData =
      error instanceof Error
        ? {
            error: error.errorData || error,
            originalErrorMessage: error.message,
          }
        : {
            errorType: typeof error,
            message,
            eventKeys: {
              error: Object.keys(error),
            },
            ...(isWebMediaPlayerEnabled && { error }),
          };

    const eventMessage =
      typeof message === 'string' && message.length
        ? message
        : 'player error: during playback';

    this.eventEmitter.emit(streamEvents.error, {
      message: eventMessage,
      context: {
        ...errorData,
        playerName: this.player.name,
        streamId,
        url,
        format,
        isWebMediaPlayerEnabled,
        abTestIds,
        wasInSteadyState: this.wasPlaying,
      },
    });
  };

  /**
   * This event is only emitted by Media Player,
   * Media Player has internal retry logic, which may cause it to emit waiting events during retry,
   * which can give us false positives about how often users are encountering buffering. To address
   * this, when we detect a retry, we'll disable the waiting listener until playback has resumed or
   * a fatal error has occurred.
   */
  _onRetrying = () => {
    this._invokePlayerMethod(
      playerMethods.off,
      htmlMediaEvents.waiting,
      this._onWaiting,
    );

    const onPlaying = () => {
      // Resume listening for waiting event
      this._invokePlayerMethod(
        playerMethods.on,
        htmlMediaEvents.waiting,
        this._onWaiting,
      );

      // Remove error listener
      this._invokePlayerMethod(
        playerMethods.off,
        htmlMediaEvents.error,
        // eslint-disable-next-line no-use-before-define
        onEnded,
      );
    };
    const onEnded = () => {
      // Remove playing listener
      this._invokePlayerMethod(
        playerMethods.off,
        htmlMediaEvents.playing,
        onPlaying,
      );

      // Remove error listener
      this._invokePlayerMethod(
        playerMethods.off,
        htmlMediaEvents.error,
        onEnded,
      );
    };

    this.player.once(htmlMediaEvents.playing, onPlaying);
    this.player.once(htmlMediaEvents.error, onEnded);
    // This event is only emitted by WMP
    this.player.once('stopped', onEnded);
  };

  /**
   * @param stream {object}
   * @param options {{
   *    url: String,
   *    format: String,
   *    additionalTimeout: Number,
   *    playbackPosition: Number
   *  }}
   * @returns {Promise}
   * @private
   */
  _attemptStreamPlayback = async (stream, options) => {
    const getPlayersTimingStart = performance.now();
    const players = this.preloadStreamData
      ? this.preloadStreamData.players
      : await this.playerLoader.getPlayers(stream).then((result) => {
          sendTimingEvent('getPlayers', getPlayersTimingStart);
          return result;
        });

    this.stream = stream;
    this.clearPlayerListeners();
    this.configuration.nowPlayingPoller.setIsUsingIcyMetadata(false);

    debug.log('attempting playback with players: ', players);
    await this._attemptPlaybackWithPlayers(players, options);
    this.setPlayerListeners();
    this.setVolume(this.volume);
  };

  /**
   * Method to attempt to load and play each media player in the "player" argument array.
   *
   * @param players {Array} An array of media players
   * @param options {{
   *    url: String,
   *    format: String,
   *    additionalTimeout: Number,
   *    playbackPosition: Number
   *  }}
   * @returns {Promise}
   * @private
   */
  _attemptPlaybackWithPlayers = async (players, options) => {
    await this._invokePlayerMethod(playerMethods.destroy);

    const {
      url,
      format,
      playbackPosition,
      additionalTimeout,
      instreamTrackingUrl,
    } = options;

    const selectedPlayer = players.shift();

    if (!selectedPlayer) {
      throw new Error('could not find a player to play stream');
    }

    try {
      await this._monitorForStreamTimeout(
        this._loadPlayerAndPlay(
          selectedPlayer,
          url,
          format,
          playbackPosition,
          instreamTrackingUrl,
        ),
        additionalTimeout,
      );
    } catch (ex) {
      debug.log('error while attempting playback with player', {
        error: ex,
        player: selectedPlayer,
        url,
      });
      this._onError(ex, 'player error: attempting playback');
      await this._attemptPlaybackWithPlayers(players, options);
    }
  };

  /**
   * @param player {Player}
   * @param url {String}
   * @param format {String}
   * @param playbackPosition {Number}
   * @param instreamTrackingUrl {String|undefined}
   * @returns {Promise<void>}
   * @private
   */
  _loadPlayerAndPlay = async (
    player,
    url,
    format,
    playbackPosition,
    instreamTrackingUrl,
  ) => {
    this.player = player;

    this._toggleMetadataListener(playerMethods.on);
    this.eventEmitter.emit(streamEvents.playerSelected, player.name);

    if (this.preloadStreamData) {
      try {
        await this.preloadStreamData.loadPromise;
      } catch (e) {
        delete this.preloadStreamData;
      }
    }

    if (!this.preloadStreamData) {
      debug.log('attempting playback with new player', player);
      const loadTimingStart = performance.now();

      await this._invokePlayerMethod(
        playerMethods.load,
        url,
        format,
        playbackPosition,
        instreamTrackingUrl,
        this.stream.useStreamMetadata,
      ).then(() => sendTimingEvent('load', loadTimingStart));
    }

    const playTimingStart = performance.now();
    await this._invokePlayerMethod(playerMethods.play, playbackPosition).then(
      () => sendTimingEvent('play', playTimingStart),
    );
    this.wasPlaying = true;
  };

  /**
   * Utility method to handling aborting stream load/play requests that timeout.
   *
   * @param promiseToMonitor {Promise}
   * @param additionalTimeout {Number}
   * @returns {Promise}
   * @private
   */
  _monitorForStreamTimeout = async (
    promiseToMonitor,
    additionalTimeout = 0,
  ) => {
    await Promise.race([
      promiseToMonitor,
      this._startTimeoutTimer(this.streamTimeout + additionalTimeout),
    ]);
    this.timer.clear();
  };

  _startTimeoutTimer = async (timeout) => {
    await new Promise((resolve) => this.timer.start(resolve, timeout));
    this.timer.clear();
    const timeoutError = new Error('stream timeout');
    timeoutError.errorData = {
      code: errorCodes.STREAM_TIMEOUT,
      isNetworkError: true,
    };
    throw timeoutError;
  };

  /**
   * Utility method that safely executes the player "method" argument if there is a player
   * loaded and the specified method exists on it.
   *
   * @param method {String}
   * @param args {*}
   * @returns {*}
   * @private
   */
  _invokePlayerMethod = (method, ...args) => {
    if (!this.player) {
      return null;
    }
    if (!this.player[method]) {
      debug.log(`unknown player method (${method})`, this.player);
      return null;
    }
    return this.player[method](...args);
  };

  on = (eventName, cb) => {
    this.eventEmitter.on(eventName, cb);
  };

  once = (eventName, cb) => {
    this.eventEmitter.once(eventName, cb);
  };

  remove = (eventName, cb) => {
    this.eventEmitter.off(eventName, cb);
  };

  removeAllListeners = (eventName) => {
    this.eventEmitter.removeAllListeners(eventName);
  };
}

export default StreamPlayer;
